import { useState, useMemo } from "react";
import { Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatCurrency } from "@/lib/formatters";

const ProductSelectorDialog = ({ open, onClose, products, categories, existingProductIds, onSelect }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      if (!product.isActive) return false;
      if (existingProductIds.includes(product.id)) return false;
      const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === "all" || product.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [products, searchQuery, selectedCategory, existingProductIds]);

  const handleSelect = (product) => {
    onSelect(product);
    setSearchQuery("");
    setSelectedCategory("all");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-[#193f7d]" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="product-selector-title">
            Select Product
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-600">
            Choose an active product to add to this event.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by product name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-slate-50 border border-slate-200 focus:border-[#fa9d1c] focus:ring-1 focus:ring-[#fa9d1c] rounded-xl h-12 pl-12 pr-4 text-base w-full"
              data-testid="product-search-input"
            />
          </div>

          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="bg-slate-50 border border-slate-200 focus:border-[#fa9d1c] rounded-xl h-12" data-testid="product-category-filter">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.name}>
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="overflow-y-auto flex-1 space-y-2 mt-2">
          {filteredProducts.length === 0 ? (
            <div className="text-center py-8 text-slate-500" data-testid="no-products-found">
              No active products found
            </div>
          ) : (
            filteredProducts.map((product) => (
              <button
                key={product.id}
                type="button"
                onClick={() => handleSelect(product)}
                className="w-full text-left bg-white border border-slate-200 hover:border-[#193f7d] hover:bg-slate-50 rounded-xl p-4 transition-colors"
                data-testid={`select-product-${product.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">{product.name}</p>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mt-1">
                      {product.category}
                    </p>
                    <p className="text-sm text-slate-600 mt-1">Unit: {product.unit}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-semibold text-slate-900">{formatCurrency(product.unitCost)}</p>
                    {product.defaultPrice != null && (
                      <p className="text-xs text-slate-500 mt-1">Sell: {formatCurrency(product.defaultPrice)}</p>
                    )}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProductSelectorDialog;
